"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isMoapBuildSliderConfigFailure = isMoapBuildSliderConfigFailure;
exports.extractMoapSliderOfferConstants = extractMoapSliderOfferConstants;
exports.buildMoapSliderConfig = buildMoapSliderConfig;
exports.getMoapRecordingChargeCents = getMoapRecordingChargeCents;
exports.getMoapSliderSelection = getMoapSliderSelection;
const isNil_1 = __importDefault(require("lodash/isNil"));
const math_1 = require("./math");
const offerDetails_1 = require("./offerDetails");
function isMoapBuildSliderConfigFailure(result) {
    return 'error' in result;
}
function tryExtractMoapOfferConstants(offer, minLineSizeCents) {
    const lineSizeCents = (0, math_1.roundDecimalProduct)(offer.lineSize ?? 0, 100);
    if (!offer.identifier) {
        return { error: 'identifier is falsy' };
    }
    if (!offer.uwSpecId) {
        return { error: 'uwSpecId is falsy' };
    }
    // MOAP is variable-APR: require the revolving APR, not a fixed term.
    if (offer.apr === undefined || offer.apr === null) {
        return { error: 'apr is nil' };
    }
    const oneTimeFeeDecimal = offer.originationFeePercent ?? 0;
    if ((0, isNil_1.default)(offer.recordingChargesFixedComponentDollars) || (0, isNil_1.default)(offer.recordingChargesVariableComponentDecimal)) {
        return { error: 'recordingCharges components nil' };
    }
    const refiPayoffCents = (0, offerDetails_1.getOfferRefiPayoffCents)(offer);
    const refiTerms = offer.lienRefiOfferTerms;
    const payoffTermYears = (offer.drawPeriodInYears ?? 0) + (offer.repaymentOnlyPeriodInYears ?? 0);
    if (refiPayoffCents > 0 && (!refiTerms || (0, isNil_1.default)(offer.drawPeriodInYears) || (0, isNil_1.default)(offer.repaymentOnlyPeriodInYears) || payoffTermYears <= 0)) {
        return { error: 'canonical refi payoff terms or maturity period missing' };
    }
    // MOAP has no fixed-term floor; respect both the policy and offer-specific line minimum.
    const perOfferMinLineSizeCents = typeof offer.minLineSize === 'number' && Number.isFinite(offer.minLineSize) && offer.minLineSize > 0 ? (0, math_1.roundDecimalProduct)(offer.minLineSize, 100) : null;
    const effectiveMinLineSizeCents = Math.max(perOfferMinLineSizeCents ?? 0, minLineSizeCents, 0);
    if (effectiveMinLineSizeCents > lineSizeCents) {
        return { error: `effectiveMinLineSizeCents=${effectiveMinLineSizeCents} > lineSizeCents=${lineSizeCents}` };
    }
    const sharedConstants = {
        identifier: offer.identifier,
        uwSpecId: offer.uwSpecId,
        apr: offer.apr,
        pricing: null,
        minLineSizeCents: effectiveMinLineSizeCents,
        maxLineSizeCents: lineSizeCents,
        oneTimeFeeDecimal,
        recordingTaxFixedCents: (0, math_1.roundDecimalProduct)(offer.recordingChargesFixedComponentDollars, 100),
        recordingTaxVariableDecimal: offer.recordingChargesVariableComponentDecimal,
        balanceTransferFeePercent: offer.balanceTransferFeePercent ?? 0,
        priorLienPayoffFee: refiTerms?.payoffFee ?? offer.priorLienPayoffFee ?? 0,
        recordingTaxFree: false,
        refiPayoffCents,
        refiPayoffWithFeeCents: (0, math_1.roundDecimalProduct)(refiTerms?.payoffAmountWithFee ?? 0, 100),
        debtSwapPayoffCents: 0,
        debtSwapPayoffWithFeeCents: 0,
        payoffFixedApr: refiTerms?.payoffFixedApr ?? null,
        payoffFixedTermMonthlyPayment: refiTerms?.payoffFixedTermMonthlyPayment ?? null,
        payoffFixedTermMonths: refiTerms ? payoffTermYears * 12 : null,
        refiPositions: (0, offerDetails_1.getOfferRefiPositions)(offer),
        refiLiensDetails: offer.lienRefiOfferTerms?.refiLiensDetails ?? [],
        refiLenderName: (0, offerDetails_1.getOfferRefiLenderName)(offer),
        secondLienBalanceBufferPercent: offer.refiLineSizeBufferDecimal ?? offer.helocRefiDetails?.secondLienBalanceBufferPercent ?? 0,
    };
    // Pricing bands carry a revolving `apr` per band (not a fixed term). Reuse the banded shape
    // only when the offer segments its line by variable APR; otherwise a single constant suffices.
    if (offer.lineSizePricingBands && offer.lineSizePricingBands.length > 0) {
        const sortedBands = [...offer.lineSizePricingBands].sort((l, r) => l.minLineSize - r.minLineSize);
        const banded = [];
        for (const band of sortedBands) {
            const minLine = Math.max(effectiveMinLineSizeCents, (0, math_1.roundDecimalProduct)(band.minLineSize, 100));
            const maxLine = (0, math_1.roundDecimalProduct)(band.maxLineSize, 100);
            if (maxLine < effectiveMinLineSizeCents || minLine > maxLine) {
                continue;
            }
            if (band.pricing.apr === null || band.pricing.apr === undefined) {
                return { error: `line-size pricing band ${band.minLineSize}-${band.maxLineSize} has nil apr` };
            }
            banded.push({ ...sharedConstants, apr: band.pricing.apr, pricing: band.pricing, minLineSizeCents: minLine, maxLineSizeCents: maxLine, recordingTaxFree: band.recordingTaxFree ?? false });
        }
        if (banded.length === 0) {
            return { error: 'lineSizePricingBands do not intersect the selectable line-size range' };
        }
        return banded;
    }
    return sharedConstants;
}
function extractMoapSliderOfferConstants(offers, minLineSizeCents, drops) {
    const all = [];
    for (const offer of offers) {
        const result = tryExtractMoapOfferConstants(offer, minLineSizeCents);
        if ('error' in result) {
            drops?.push({ identifier: offer.identifier, error: result.error });
        }
        else if (Array.isArray(result)) {
            all.push(...result);
        }
        else {
            all.push(result);
        }
    }
    return all;
}
/**
 * Build the MOAP slider config from the underwriting result + backend-computed debt-swap payoff.
 *
 * Fails loudly when the offers cannot be reconciled with the local variable-APR math (same
 * "never soften a failure into a clamp" contract as the AC builder): a failure means the
 * frontend must render the approved terms read-only rather than an interactive slider.
 */
function buildMoapSliderConfig(underwritingResult, debtSwapPayoffCents, minLineSizeCents, debtSwapLineSizeBufferDecimal) {
    const totalOffers = underwritingResult.preQualOffers?.length ?? 0;
    const drops = [];
    const fail = (error) => ({ error, totalOffers, drops });
    const constants = extractMoapSliderOfferConstants(underwritingResult.preQualOffers ?? [], minLineSizeCents, drops);
    if (constants.length === 0) {
        return fail('all offers dropped by extractMoapSliderOfferConstants');
    }
    const feasibleOffers = [];
    for (const offer of constants) {
        const debtSwapFeeCents = (0, offerDetails_1.computeBalanceTransferFee)(debtSwapPayoffCents, offer.balanceTransferFeePercent).amountCents;
        const payoffAndDebtSwapCents = offer.refiPayoffWithFeeCents + debtSwapPayoffCents + debtSwapFeeCents;
        const amountToFundCents = Math.max(payoffAndDebtSwapCents, offer.refiPayoffWithFeeCents +
            (0, math_1.roundDecimalProduct)(offer.refiPayoffCents, offer.secondLienBalanceBufferPercent) +
            (0, math_1.roundDecimalProduct)(debtSwapPayoffCents, 1 + debtSwapLineSizeBufferDecimal));
        const rangeMin = offer.minLineSizeCents;
        const rangeMax = offer.maxLineSizeCents;
        const requiredLineSizeCents = (0, offerDetails_1.getLineSizeRequiredToFundCents)({
            amountToFundCents,
            upfrontDrawFeeDecimal: offer.oneTimeFeeDecimal,
            minDrawDownDecimal: 1,
            recordingTaxFixedCents: offer.recordingTaxFree ? 0 : offer.recordingTaxFixedCents,
            recordingTaxVariableDecimal: offer.recordingTaxFree ? 0 : offer.recordingTaxVariableDecimal,
        });
        let min = Math.max(rangeMin, requiredLineSizeCents);
        if (offer.refiPayoffCents > 0) {
            // Round the additional-limit floor (balance buffer + recording) up to the nearest $1,000.
            const roundedAdditionalLimitCents = Math.ceil((min - payoffAndDebtSwapCents) / 100000) * 100000;
            min = Math.max(min, payoffAndDebtSwapCents + roundedAdditionalLimitCents);
        }
        if (min <= rangeMax) {
            feasibleOffers.push({ ...offer, debtSwapPayoffCents, debtSwapPayoffWithFeeCents: debtSwapPayoffCents + debtSwapFeeCents, minLineSizeCents: min, maxLineSizeCents: rangeMax });
        }
    }
    if (feasibleOffers.length === 0) {
        return fail('committed payoffs and fees exceed all approved lines');
    }
    return {
        minLineSizeCents: Math.min(...feasibleOffers.map((offer) => offer.minLineSizeCents)),
        maxLineSizeCents: Math.max(...feasibleOffers.map((offer) => offer.maxLineSizeCents)),
        sliderOfferConstants: feasibleOffers,
    };
}
function getMoapRecordingChargeCents(offer, lineSizeCents) {
    if (offer.recordingTaxFree) {
        return 0;
    }
    return Math.max(0, (0, offerDetails_1.computeRecordingFee)(lineSizeCents, offer.recordingTaxFixedCents, offer.recordingTaxVariableDecimal).amountCents);
}
/** Select one feasible offer consistently for all displayed and submitted terms. */
function getMoapSliderSelection(config, requestedLineSizeCents) {
    let selected = null;
    let selectedLineSizeCents = 0;
    let selectedDistance = Infinity;
    let selectedFees = Infinity;
    for (const offer of config.sliderOfferConstants) {
        const lineSizeCents = Math.max(offer.minLineSizeCents, Math.min(offer.maxLineSizeCents, Math.round(requestedLineSizeCents)));
        const distance = Math.abs(lineSizeCents - requestedLineSizeCents);
        const fees = (0, offerDetails_1.computeOneTimeFee)(lineSizeCents, offer.oneTimeFeeDecimal).amountCents +
            getMoapRecordingChargeCents(offer, lineSizeCents) +
            offer.refiPayoffWithFeeCents -
            offer.refiPayoffCents +
            offer.debtSwapPayoffWithFeeCents -
            offer.debtSwapPayoffCents;
        if (distance < selectedDistance || (distance === selectedDistance && (!selected || offer.apr < selected.apr || (offer.apr === selected.apr && fees < selectedFees)))) {
            selected = offer;
            selectedLineSizeCents = lineSizeCents;
            selectedDistance = distance;
            selectedFees = fees;
        }
    }
    if (!selected) {
        return null;
    }
    const recordingChargeCents = getMoapRecordingChargeCents(selected, selectedLineSizeCents);
    const payoffCents = selected.refiPayoffWithFeeCents + selected.debtSwapPayoffWithFeeCents;
    return {
        constants: selected,
        lineSizeCents: selectedLineSizeCents,
        recordingChargeCents,
        payoffCents,
    };
}
